import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import Cookies from "js-cookie";
import { login, logoutReq } from "./requests";

/**
 * How the session token is stored.
 *
 * It used to be written to localStorage as well as to a cookie with no
 * flags at all. Two copies is one more than anything needs, and the
 * spare one in localStorage was the thing a cross-site script would have
 * walked off with. What is left is a single cookie, marked SameSite
 * strict so another site cannot ride it, and Secure whenever the page
 * itself is served over https.
 *
 * The real fix is a HttpOnly cookie set by the API, which no script can
 * read at all - that needs the API to set it, and is the next step here.
 */
const storeToken = (token) => {
  Cookies.set("token", String(token).replace("Bearer ", ""), {
    sameSite: "strict",
    secure: window.location.protocol === "https:",
  });
};


// Define a name for slice
const sliceName = "auth";

const read = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key));
  } catch (e) {
    return null;
  }
};

/*
 * Read fresh every time rather than once at module load.
 *
 * As a module-level constant this was a snapshot of localStorage taken
 * on the login page, and every reducer that reset with `...initialState`
 * put that snapshot back — so after signing in, state.auth.settings was
 * still the null it had been before the request, and the sidebar and
 * footer showed the bundled fallback name and logo for the whole session
 * until someone reloaded the page.
 */
const freshState = () => ({
  errors: [],
  otpRequired: false,
  loaderState: false,
  authUser: read("user"),
  permissions: read("permissions"),
  settings: read("settings"),
});

const initialState = freshState();

export const signinUser = createAsyncThunk(
  `${sliceName}/signinUser`,
  async (auth, thunkApi) => {
    const res = await login(auth);
    if (res.code === 200) {
      localStorage.setItem("user", JSON.stringify(res.data.user));
      localStorage.setItem("permissions", JSON.stringify(res.data.permissions));
      localStorage.setItem("settings", JSON.stringify(res.data.settings));
      storeToken(res.data.access_token);
      return res.data;
    } else if (res.code === 202) {
      let data = [];
      data.code = 202;
      data.msg = res.msg;
      return thunkApi.rejectWithValue(data);
    } else {
      /*
       * A refused sign-in is about the attempt, not about one box. This
       * message used to be hung under the Email field, which read as
       * "this address is wrong" whatever the reason — and once the API
       * started rate limiting, the thing sitting under Email was
       * "Request failed with status code 429". It belongs above the
       * form, where SignIn now shows it.
       */
      let errors = [];
      errors['form'] = res.msg
      return thunkApi.rejectWithValue(errors);
    }
  }
);
export const authSlice = createSlice({
  name: sliceName,
  initialState,
  reducers: {
    logout: (state, action) => {
      // Tell the server first, while the token is still attached: the
      // token stayed valid for its full life (a day, a week with
      // "remember me") when signing out only cleared the browser.
      logoutReq();
      localStorage.clear();
      Cookies.remove("token");
      return {
        ...freshState(),
        authUser: null,
      };
    },

    /**
     * Settings changed on the Settings screen. Everything that shows the
     * site's name, logo or footer reads this slice, so the whole app
     * renames itself the moment this lands — the screen used to write
     * localStorage only, which nothing re-read until a full reload.
     */
    settingsUpdated: (state, action) => {
      localStorage.setItem("settings", JSON.stringify(action.payload));
      state.settings = action.payload;
    },
  },
  extraReducers: (builder) => {
    /*
     * loaderState drives the LOG IN button's spinner, so it has to be
     * true for exactly as long as the request is in flight. This case
     * used to fall back to freshState()'s `false`, which switched the
     * spinner off the instant it was dispatched — the button looked
     * idle while the login call was still running.
     */
    builder.addCase(signinUser.pending, (state, action) => {
      localStorage.removeItem("user");
      Cookies.remove("token");
      return {
        ...freshState(),
        loaderState: true,
        otpRequired: state.otpRequired,
      };
    });
    builder.addCase(signinUser.fulfilled, (state, action) => {
      return {
        ...freshState(),
        loaderState: true,
        authUser: action.payload.user,
        permissions: action.payload.permissions,
        settings: action.payload.settings,
      };
    });
    // Rejected is the only outcome that hands the form back to the user,
    // so it is the only one that stops the spinner — freshState()'s
    // loaderState: false does that.
    builder.addCase(signinUser.rejected, (state, action) => {
      if (action.payload && action.payload.code && action.payload.code === 202) {
        return {
          ...freshState(),
          otpRequired: true,
          errors: action.payload.msg
        };
      }
      return {
        ...freshState(),
        otpRequired: state.otpRequired,
        errors: action.payload
      };
    });
  }
});

export default authSlice.reducer;
export const { logout, settingsUpdated } = authSlice.actions;
